"use client";

import { useState, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useRouter } from "next/navigation";
import { deleteCookie, getCookie, setCookie } from "cookies-next";
import { showToast, TOAST_TYPES } from "@/lib/toast-utils/toast.util";
import { GLOBAL_ERROR_MESSAGE } from "@/constants/validation-message";
import { accountDeletionService } from "@/services/settings/account-deletion.service";
import { AccountDeletionRequest, AccountDeletionResponse } from "@/interface/settings/account-deletion.interface";
import { COOKIE_CONFIG } from "@/config";
import { clearProjectCreationStore } from "@/lib/store-utils/store.util";
import useProfileStore from "@/store/useProfileStore";

const useAccountDeletion = () => {
  const router = useRouter();
  const queryClient = useQueryClient();
  const { clearProfile } = useProfileStore();

  const deactivateAccountMutation = useMutation<
    AccountDeletionResponse,
    Error,
    AccountDeletionRequest
  >({
    mutationFn: (data: AccountDeletionRequest) =>
      accountDeletionService.deactivateAccount(data),
    onSuccess: () => {
      showToast(
        TOAST_TYPES.success,
        "Account deactivation request submitted successfully"
      );

      // Clear all session data before redirecting
      clearProjectCreationStore();
      localStorage.clear();
      deleteCookie(COOKIE_CONFIG.loggedIn);
      deleteCookie(COOKIE_CONFIG.profile);
      deleteCookie(COOKIE_CONFIG.version);
      clearProfile();
      setCookie(COOKIE_CONFIG.accountDeleted, "true", {
        maxAge: 60,
        sameSite: "lax",
      });

      if (typeof window !== "undefined") {
        window.dispatchEvent(new Event("auth-state-changed"));
      }

      queryClient.clear();
      router.replace("/login");
    },
    onError: (error) => {
      showToast(
        TOAST_TYPES.error,
        error?.message || GLOBAL_ERROR_MESSAGE
      );
    },
  });

  const requestAccountDeletion = async (reason: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      deactivateAccountMutation.mutate(
        { reason },
        {
          onSuccess: () => {
            resolve();
          },
          onError: (error) => {
            reject(error);
          },
        }
      );
    });
  };

  return {
    requestAccountDeletion,
    deactivateAccountMutation,
    isLoading: deactivateAccountMutation.isPending,
  };
};

export const useAccountDeletionSuccessModal = () => {
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  useEffect(() => {
    // Check if accountDeleted cookie is present
    const accountDeleted = getCookie(COOKIE_CONFIG.accountDeleted);
    if (accountDeleted === "true") {
      setShowSuccessModal(true);
      // Clear the cookie after reading it
      deleteCookie(COOKIE_CONFIG.accountDeleted);
    }
  }, []);

  const handleCloseModal = () => {
    setShowSuccessModal(false);
  };

  const handleOkClick = () => {
    setShowSuccessModal(false);
  };

  return {
    showSuccessModal,
    setShowSuccessModal,
    handleCloseModal,
    handleOkClick,
  };
};

export default useAccountDeletion;

